import {getCollectionType} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  isCustomField,
  isItemRef,
  sortByProductSection,
  DEFAULT_DATETIME_FIELD_SLUGS,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

import {getProductsBindingFieldSlugSortValue} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {compareFields} from '@packages/systems/cms/gateway/shared';

import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';

type GroupedFields = {
  [groupLabel: string]: Field[];
};

export function groupCollectionFieldsByProductSection(
  fields: Field[],
  collection?: Collection | null
): GroupedFields {
  // sorts binding paths for ecommerce Product and Order collections based on fields in sections
  // on the product form (additional fields are sorted into Cart and Timestamp sections)
  const getSlug = (
    field: Field
  ): {
    slug: string;
    fromCustomReferenceField: boolean;
  } => {
    const {slug} = field;
    const fromCustomReferenceField =
      isItemRef(field) &&
      (collection
        ? isCustomField(field, getCollectionType(collection))
        : isCustomField(field));

    return {slug, fromCustomReferenceField};
  };
  return sortByProductSection(fields, getSlug);
}

export function getBindableSkuFields(skus: Collection): Field[] {
  return skus.fields
    .toArray()
    .filter((field) => isCustomField(field) && !isItemRef(field));
}

export function sortProductAndSkuFields(fields: Field[]) {
  const copy = [...fields];
  copy.sort((fieldA, fieldB) => {
    // Sort timestamp related fields last
    if (DEFAULT_DATETIME_FIELD_SLUGS.includes(fieldA.slug)) {
      return DEFAULT_DATETIME_FIELD_SLUGS.includes(fieldB.slug)
        ? getProductsBindingFieldSlugSortValue(fieldA.slug) -
            getProductsBindingFieldSlugSortValue(fieldB.slug)
        : 1;
    }

    // Sorts all default Product fields as specified
    if (
      getProductsBindingFieldSlugSortValue(fieldA.slug) !==
      getProductsBindingFieldSlugSortValue(fieldB.slug)
    ) {
      return (
        getProductsBindingFieldSlugSortValue(fieldA.slug) -
        getProductsBindingFieldSlugSortValue(fieldB.slug)
      );
    }

    // For remaining fields, sort like a CMS collection
    return compareFields(fieldA, fieldB);
  });
  return copy;
}
